import { Input } from '@/components/cms/ui/input';
import { SearchField } from '@/components/ui/search-field';
import { Card } from '@/components/ui/card';
import { FieldLabel } from '@/components/ui/text';
import { Panel, PanelAnchor, PanelBody, PanelContent, PanelEmpty, PanelSection } from '@/components/ui/panel';
import { ListRow } from '@/components/ui/list-row';
import { ProjectMap, ProjectMapRef } from '@/components/project/project-map';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { MapPinLineIcon } from '@phosphor-icons/react';
import type { Story } from '@/types/cmsTypes';
import { CardHeading } from '@/components/ui/card';

interface StoryLocationCardProps {
  projectMapRef: React.RefObject<ProjectMapRef | null>;
  currentStory: Story;
  latitude: string;
  longitude: string;
  address: string;
  city: string;
  onAddressChange: (address: string) => void;
  onMapPick: (lat: number, lng: number) => void;
  onPlacePick: (lat: number, lng: number, address: string) => void;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  onSearchKeyPress: () => void;
  onAutocompleteSelect: (prediction: google.maps.places.AutocompletePrediction) => void;
  onCoordinateChange: (newLat: string, newLng: string) => void;
  autocompleteResults: google.maps.places.AutocompletePrediction[];
  showAutocomplete: boolean;
  /** Closes the results, on Escape or a click outside the search and its results. */
  onAutocompleteClose: () => void;
  isSearching: boolean;
  isGoogleMapsLoaded: boolean;
  projectId: string | undefined;
  userId: string | undefined;
  validateGPSCoordinates: () => boolean;
  showValidation: boolean;
  hasSceneOrigin: boolean;
  isLocationRequired: boolean;
  getFieldErrorClass: (isValid: boolean, showValidation: boolean) => string;
  searchInputRef?: React.RefObject<HTMLInputElement | null>;
}

export function StoryLocationCard({
  projectMapRef,
  currentStory,
  latitude,
  longitude,
  address,
  onAddressChange,
  onMapPick,
  onPlacePick,
  searchQuery,
  onSearchQueryChange,
  onSearchKeyPress,
  onAutocompleteSelect,
  onCoordinateChange,
  autocompleteResults,
  showAutocomplete,
  onAutocompleteClose,
  isSearching,
  isGoogleMapsLoaded,
  projectId,
  userId,
  validateGPSCoordinates,
  showValidation,
  hasSceneOrigin,
  isLocationRequired,
  getFieldErrorClass,
  searchInputRef,
}: StoryLocationCardProps) {
  const showLocationError = !validateGPSCoordinates() && showValidation && hasSceneOrigin && isLocationRequired;

  const handleStorySelect = (story: Story) => {
    if (story.locationInfo?.coordinates) {
      onPlacePick(story.locationInfo.coordinates.lat, story.locationInfo.coordinates.lng, story.locationInfo.address || '');
    }
  };

  const showResults = showAutocomplete && autocompleteResults.length > 0;
  const showEnterHint = Boolean(searchQuery) && !showAutocomplete;

  return (
    <Card>
      <CardHeading as="h2" icon={<MapPinLineIcon />} title="Location Settings" />

      <div className="space-y-4">
        <div>
          <FieldLabel variant="plain" mb>Select Location on Map</FieldLabel>
          <div
            className={`h-96 relative border-lureo-border-width rounded-lureo-radius-lg overflow-hidden ${
              showLocationError
                ? 'border-lureo-danger-ink'
                : 'border-lureo-line-strong'
            }`}
          >
            <ProjectMap
              ref={projectMapRef}
              stories={[currentStory]}
              onStorySelect={handleStorySelect}
              allowMapClick
              isStoryEditPage={true}
              projectId={projectId}
              userId={userId}
              onMapClick={onMapPick}
              centerLocation={
                latitude &&
                longitude &&
                !isNaN(parseFloat(latitude)) &&
                !isNaN(parseFloat(longitude))
                  ? { lat: parseFloat(latitude), lng: parseFloat(longitude) }
                  : undefined
              }
              hideNumbers={true}
            />

            {/* Search Bar Overlay */}
            <div className="absolute top-4 left-4 z-10">
              {/* Open while there are results or a query to search */}
              <Panel open={showResults || showEnterHint} onOpenChange={(open) => { if (!open) onAutocompleteClose(); }}>
                <PanelAnchor asChild>
                  <div className="relative flex w-80 items-center">
                    <SearchField
                      ref={searchInputRef}
                      size="md"
                      look="outlined"
                      placeholder="Search for a location..."
                      value={searchQuery || ''}
                      onChange={onSearchQueryChange}
                      onKeyPress={(e) => e.key === 'Enter' && onSearchKeyPress()}
                      disabled={!isGoogleMapsLoaded || isSearching}
                    />
                    {isSearching && (
                      <div className="absolute right-8">
                        <LoadingSpinner size="sm" />
                      </div>
                    )}
                  </div>
                </PanelAnchor>

                <PanelContent align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
                  <PanelBody>
                    {showResults ? (
                      <PanelSection>
                        <p className="text-lureo-12 text-lureo-ink-subtle">Found {autocompleteResults.length} results (click to select)</p>
                        {autocompleteResults.map((prediction) => (
                          <ListRow
                            key={prediction.place_id}
                            tabIndex={-1}
                            label={prediction.structured_formatting?.main_text || prediction.description}
                            description={prediction.structured_formatting?.secondary_text}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onAutocompleteSelect(prediction);
                            }}
                          />
                        ))}
                      </PanelSection>
                    ) : (
                      <PanelEmpty>Press Enter to search and update coordinates</PanelEmpty>
                    )}
                  </PanelBody>
                </PanelContent>
              </Panel>
            </div>
          </div>
        </div>

        <div>
          <FieldLabel variant="plain" mb>GPS Coordinates</FieldLabel>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Input
                type="number"
                className={`select-text ${getFieldErrorClass(showLocationError, true)}`}
                value={latitude || ''}
                onChange={(e) => onCoordinateChange(e.target.value, longitude)}
                placeholder="37.3469"
                step="0.0001"
                autoComplete="off"
                onFocus={(e) => e.target.select()}
              />
              <p className="mt-1 pl-1 text-lureo-12 text-lureo-ink-subtle">Latitude</p>
            </div>
            <div>
              <Input
                type="number"
                className={`select-text ${getFieldErrorClass(showLocationError, true)}`}
                value={longitude || ''}
                onChange={(e) => onCoordinateChange(latitude, e.target.value)}
                placeholder="-6.2553"
                step="0.0001"
                autoComplete="off"
                onFocus={(e) => e.target.select()}
              />
              <p className="mt-1 text-lureo-12 text-lureo-ink-subtle">Longitude</p>
            </div>
          </div>
        </div>

        <div>
          <FieldLabel variant="plain" mb htmlFor="story-address" className="mt-6">Address (Optional)</FieldLabel>
          <Input
            id="story-address"
            value={address || ''}
            onChange={(e) => onAddressChange(e.target.value)}
            placeholder="e.g., 123 Main Street, City, Country"
            autoComplete="off"
            className="select-text"
            onFocus={(e) => e.target.select()}
          />
          <p className="mt-1 text-lureo-12 text-lureo-ink-subtle">Human-readable address for better context</p>
        </div>
      </div>
    </Card>
  );
}
